import { RestClient } from "./restClient";

export type PipelineRunStatus = "running" | "succeeded" | "partial" | "failed";

export interface PipelineRun {
  id: string;
  status: PipelineRunStatus;
  /** Per-step results, keyed by step name (sync/humanize/archive/map/report).
   *  A "partial" run has some steps ok and some failed -- the report can be
   *  perfectly good even when archive failed, so the UI reports which. */
  steps: Record<string, { status?: string; error?: string }>;
  error: string | null;
}

export interface PipelineRunStarted {
  run_id: string;
  status: string;
  /** True when a run was already in flight — the service allows one at a
   *  time, so the button joins the existing run rather than starting a
   *  second. */
  already_running: boolean;
}

/** The products the pipeline attributes clicks to; "unknown" when it could not tell. */
export const TAG_COVERAGE_PRODUCTS = ["artie", "fosa", "holdpoint", "eolas_v1", "unknown"] as const;
export type TagCoverageProduct = (typeof TAG_COVERAGE_PRODUCTS)[number];

/** One area's clicks over the window, split by how they were tagged. */
export interface TagCoverageArea {
  product: string;
  area: string | null;
  clicks: number;
  explicit: number;
  untagged: number;
  guessed: number;
}

/** A frequently clicked element that has no explicit tag yet. */
export interface TagCoverageGap {
  product: string;
  area: string | null;
  path: string | null;
  component: string | null;
  route: string | null;
  tag_source: "untagged" | "guessed";
  clicks: number;
  last_seen: string;
}

export interface TagCoverage {
  days: number;
  product: string | null;
  summary: TagCoverageArea[];
  next_to_tag: TagCoverageGap[];
}

/** The PostHog analytics pipeline and the weekly company report it produces.
 *
 *  Routed at `/posthog/*` (chat-ui nginx → posthog_service), NOT through
 *  unified_agent: the service authorizes the browser's own session JWT
 *  against OpenFGA itself, so there is nothing for a proxy to add.
 *
 *  Both endpoints are admin-only server-side; the UI gate is cosmetic. */
export class PosthogReportsApi {
  constructor(private client: RestClient) {}

  /** Starts a run (sync → humanize → archive → map → report) and returns at
   *  once with 202; the run continues server-side for minutes. */
  runPipeline(): Promise<PipelineRunStarted> {
    return this.client.post<PipelineRunStarted>("/posthog/v1/pipeline/runs", {});
  }

  /** One run's status, for polling a run this session started. */
  getRun(runId: string): Promise<PipelineRun> {
    return this.client.get<PipelineRun>(
      `/posthog/v1/pipeline/runs/${encodeURIComponent(runId)}`,
    );
  }

  /** Click tag coverage per area over the last `days`, for every product
   *  unless one is given. Platform-admin only server-side. */
  getTagCoverage({ product, days }: { product?: TagCoverageProduct; days: number }): Promise<TagCoverage> {
    const query = new URLSearchParams({ days: String(days) });
    if (product) query.set("product", product);
    return this.client.get<TagCoverage>(`/posthog/v1/tags/coverage?${query}`);
  }

  /** The newest report PDF as an object URL, or null when no run has
   *  produced one yet (404).
   *
   *  Fetched as a blob rather than linked directly: the bucket is private
   *  and the endpoint needs the session's auth headers, which a plain
   *  `<a href>` or `<iframe src>` cannot carry. The caller owns the returned
   *  URL and must `URL.revokeObjectURL` it. */
  async latestReportUrl(): Promise<string | null> {
    const response = await this.client.rawResponse("/posthog/v1/reports/latest", {
      method: "GET",
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`report fetch failed (${response.status})`);
    }
    return URL.createObjectURL(await response.blob());
  }
}
